import { MatchRunner } from '../combat/match.js';
import { releasedInput } from '../combat/state.js';
import { otherSlot, } from '../combat/types.js';
import { NET } from './constants.js';
import { explainsCorrection } from './events.js';
/** The enemy's last input with every action released: movement is held, the trigger is not. */
export function heldInput(last) {
    return {
        ...last,
        jump: false,
        primary: false,
        secondary: false,
        ability: false,
        ultimate: false,
        vent: false,
        melee: false,
    };
}
export class Predictor {
    slot;
    enemy;
    runner;
    /** The newest authoritative state applied. */
    authoritative;
    metrics = {
        count: 0,
        max: 0,
        unexpectedMax: 0,
        last: 0,
        lastTick: -1,
        resimulated: 0,
        resets: 0,
    };
    pending = new Map();
    expectedUntil = -1;
    correction = null;
    enemyPoseAt;
    constructor(opts) {
        this.slot = opts.slot;
        this.enemy = otherSlot(opts.slot);
        this.runner = new MatchRunner(opts.setup, opts.world, opts.arena, opts.bot, opts.catalog);
        this.authoritative = this.runner.state;
        this.enemyPoseAt = opts.enemyPoseAt ?? (() => null);
    }
    /** The predicted state (the own mech's source of truth for drawing). */
    get predicted() {
        return this.runner.state;
    }
    get tick() {
        return this.runner.state.tick;
    }
    pendingCount() {
        return this.pending.size;
    }
    /** The displacement the last reconciliation applied to the own mech (old minus new), once. */
    takeCorrection() {
        const c = this.correction;
        this.correction = null;
        return c;
    }
    /** Anchors on an authoritative state and forgets every pending input (join, reconnect, resync). */
    reset(auth) {
        this.authoritative = auth;
        this.runner.restore(auth);
        this.pending.clear();
        this.correction = null;
        this.metrics.resets++;
    }
    /** Steps one predicted tick with the player's input and returns the predicted events. */
    predict(sample) {
        const tick = this.runner.state.tick + 1;
        const input = { ...sample, seq: tick };
        this.pending.set(tick, input);
        while (this.pending.size > NET.MAX_PENDING_INPUTS) {
            const oldest = this.pending.keys().next().value;
            if (oldest === undefined)
                break;
            this.pending.delete(oldest);
        }
        return this.step(input, this.authoritative.mechs[this.enemy].lastInput);
    }
    /** The input recorded for a predicted tick (what the wire carries). */
    inputFor(tick) {
        return this.pending.get(tick) ?? null;
    }
    /**
     * Applies a verified authoritative state and its events: drops the inputs the server has
     * passed, restores the truth and re-simulates the rest. Returns false when the prediction had
     * fallen behind the server (the caller then jumps ahead).
     */
    reconcile(auth, events) {
        const before = this.runner.state;
        const predictedTick = before.tick;
        this.authoritative = auth;
        for (const tick of [...this.pending.keys()])
            if (tick <= auth.tick)
                this.pending.delete(tick);
        if (events.some((e) => explainsCorrection(e, this.slot))) {
            this.expectedUntil = auth.tick + NET.EXPECTED_CORRECTION_TICKS;
        }
        this.runner.restore(auth);
        if (predictedTick <= auth.tick) {
            this.pending.clear();
            this.correction = null;
            return false;
        }
        const enemyLast = auth.mechs[this.enemy].lastInput;
        for (let tick = auth.tick + 1; tick <= predictedTick; tick++) {
            const input = this.pending.get(tick) ?? releasedInput(this.runner.state.mechs[this.slot].lastInput);
            this.step(input, enemyLast);
            this.metrics.resimulated++;
        }
        const a = before.mechs[this.slot].body.position;
        const b = this.runner.state.mechs[this.slot].body.position;
        const distance = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
        if (distance > 1e-3) {
            const m = this.metrics;
            m.count++;
            m.last = distance;
            m.lastTick = predictedTick;
            if (distance > m.max)
                m.max = distance;
            if (predictedTick > this.expectedUntil && distance > m.unexpectedMax) {
                m.unexpectedMax = distance;
            }
            this.correction = { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
        }
        return true;
    }
    step(input, enemyLast) {
        const enemyPose = this.enemyPoseAt(this.runner.state.tick + 1);
        const held = heldInput(enemyLast);
        const inputs = this.slot === 0 ? [input, held] : [held, input];
        const rewound = this.slot === 0 ? [enemyPose, null] : [null, enemyPose];
        return this.runner.step(inputs, { rewound, prediction: this.slot });
    }
}
//# sourceMappingURL=predictor.js.map